/**
 * Drag the Words content type schema — ACH slug `qt-drag-the-words`
 */

import type {
    CompositeQuestionsLayoutFields,
    ContentConfig,
    LayoutConfig,
    ScoringConfig,
} from '@ai-content-hub/content-core';
import type { FITBElementData } from '@ai-content-hub/content-elements';
import { defaultPageData } from '@ai-content-hub/content-elements';
import type { PageElementData } from '@ai-content-hub/content-elements';
import { generateId } from '@ai-content-hub/content-core';
import { fitbElementSchema } from '@ai-content-hub/content-elements';

export type { PageElementData } from '@ai-content-hub/content-elements';

export type QtDragTheWordsSplitRatio = '50-50' | '60-40' | '40-60' | '70-30' | '30-70';

export interface QtDragTheWordsLayoutConfig extends LayoutConfig {
    splitRatio?: QtDragTheWordsSplitRatio;
    displayMode?: 'all' | 'step' | 'tabs';
    inlineSinglePageNav?: boolean;
}

export interface QtDragTheWordsScoringConfig extends ScoringConfig {
    pointsPerQuestion?: number;
    passingThreshold?: number;
}

export interface QtDragTheWordsItem {
    id: string;
    data: FITBElementData;
    /** Wrong options shared across all blanks in this exercise (distractors). */
    sharedDistractors?: string[];
    points?: number;
}

export interface QtDragTheWordsData extends CompositeQuestionsLayoutFields {
    title?: string;
    instructions?: string;
    pageContent: PageElementData;
    items: QtDragTheWordsItem[];
}

export interface QtDragTheWordsConfig extends Omit<ContentConfig, 'layout' | 'scoring'> {
    layout?: QtDragTheWordsLayoutConfig;
    scoring?: QtDragTheWordsScoringConfig;
    /** Passed to FITB (chip order) */
    shuffleOptions?: boolean;
    caseSensitive?: boolean;
}

export interface QtDragTheWordsContent {
    id: string;
    type: 'qt-drag-the-words';
    version: string;
    data: QtDragTheWordsData;
    config?: QtDragTheWordsConfig;
}

export const defaultQtDragTheWordsI18n = {
    en: {
        submit: 'Submit',
        retry: 'Try Again',
        noQuestions: 'No questions available',
        contentTab: 'Content',
        questionsTab: 'Questions',
        configTab: 'Configuration',
        titleLabel: 'Title',
        titlePlaceholder: 'Exercise title',
        instructionsLabel: 'Instructions (optional)',
        instructionsPlaceholder: 'Learner-facing instructions',
        exercisesListTitle: 'Exercises',
        emptyExercisesMsg: 'No exercises yet. Add a drag-the-words passage.',
        addSection: 'Add section',
        removeSection: 'Remove section',
        dragTheWordsBlanksPreview: 'Drag the words blanks',
        addExercise: 'Add exercise',
        sectionDetailTitle: 'Section',
        exerciseDetailTitle: 'Exercise',
        dragTheWordsBadge: 'Drag the Words',
        detailEmptyMessage: 'Select an exercise, a section, or add one to start editing.',
        removeExercise: 'Remove exercise',
        exerciseHeading: 'Exercise {{index}}',
        sharedDistractorsLabel: 'Wrong options',
        sharedDistractorsHint: 'Wrong options; separate with semicolons. These apply to all blanks.',
        sharedDistractorsPlaceholder: 'e.g. wrong1; wrong2; wrong3',
    },
    vi: {
        submit: 'Gửi',
        retry: 'Thử lại',
        noQuestions: 'Chưa có câu hỏi',
        contentTab: 'Nội dung',
        questionsTab: 'Câu hỏi',
        configTab: 'Cấu hình',
        titleLabel: 'Tiêu đề',
        titlePlaceholder: 'Tiêu đề bài tập',
        instructionsLabel: 'Hướng dẫn (tuỳ chọn)',
        instructionsPlaceholder: 'Hướng dẫn cho học viên',
        exercisesListTitle: 'Danh sách bài tập',
        emptyExercisesMsg: 'Chưa có bài tập nào. Hãy thêm đoạn văn kéo từ.',
        addSection: 'Thêm mục',
        removeSection: 'Xóa mục',
        dragTheWordsBlanksPreview: 'Kéo từ vào chỗ trống',
        addExercise: 'Thêm bài tập',
        sectionDetailTitle: 'Mục',
        exerciseDetailTitle: 'Bài tập',
        dragTheWordsBadge: 'Kéo từ',
        detailEmptyMessage: 'Chọn một bài tập, một mục, hoặc thêm mới để bắt đầu.',
        removeExercise: 'Xóa bài tập',
        exerciseHeading: 'Bài tập {{index}}',
        sharedDistractorsLabel: 'Tùy chọn sai',
        sharedDistractorsHint: 'Tùy chọn sai; phân cách bằng dấu chấm phẩy. Áp dụng cho tất cả chỗ trống.',
        sharedDistractorsPlaceholder: 'VD: sai1; sai2; sai3',
    },
};

export const defaultQtDragTheWordsConfig: QtDragTheWordsConfig = {
    layout: {
        type: 'horizontal',
        splitRatio: '50-50',
        displayMode: 'all',
    },
    shuffleOptions: true,
    caseSensitive: false,
    behavior: {
        allowRetry: true,
        showCorrectAnswer: true,
        showExplanation: true,
        examMode: false,
        instantFeedback: false,
    },
    scoring: {
        pointsPerQuestion: 1,
        partialCreditEnabled: true,
        passingThreshold: 0.7,
    },
    i18n: {
        defaultLanguage: 'en',
        languages: defaultQtDragTheWordsI18n,
    },
};

export const defaultQtDragTheWordsData: QtDragTheWordsData = {
    // EAA-2082: a new Drag the words starts with an EMPTY title so the editor shows only the
    // placeholder. A non-empty default ("Drag the words") was picked up by the question-bank page
    // as the content title and saved although nobody typed it.
    title: '',
    instructions: '',
    pageContent: { ...defaultPageData },
    items: [],
};

export function createQtDragTheWordsItem(id?: string): QtDragTheWordsItem {
    return {
        id: id ?? generateId(),
        data: { ...(fitbElementSchema.defaultData as FITBElementData) },
    };
}

/**
 * Wrap legacy answers keyed by blank id when there is a single item.
 */
export function normalizeQtDragTheWordsAnswerMap(
    data: QtDragTheWordsData,
    answer: Record<string, unknown> | undefined | null,
): Record<string, unknown> {
    if (!answer || typeof answer !== 'object' || Array.isArray(answer)) {
        return {};
    }
    const items = data.items ?? [];
    if (items.length === 1) {
        const item = items[0];
        const nested = answer[item.id];
        if (nested != null && typeof nested === 'object' && !Array.isArray(nested)) {
            return { ...answer };
        }
        const blanks = item.data.blanks ?? [];
        for (const b of blanks) {
            if (Object.prototype.hasOwnProperty.call(answer, b.id)) {
                return { [item.id]: { ...answer } };
            }
        }
    }
    return { ...answer };
}

export const qtDragTheWordsSchema = {
    type: 'qt-drag-the-words' as const,
    title: 'Drag the Words',
    description: 'Reading content with multiple drag-and-drop fill-in-the-blank exercises',
    version: '1.0.0',
    category: 'assessment' as const,
    icon: 'GripHorizontal',
    tags: ['qt-drag-the-words', 'fitb', 'drag', 'ach'],
    defaultData: defaultQtDragTheWordsData,
    defaultConfig: defaultQtDragTheWordsConfig,
};
